/* Services */
import {
    authService,
    postMessageService,
    settingsService,
    userService,
} from "../_services";

/* Actions */
import { oauthActions, userActions } from "./";

/* Constants */
import { authConstants as AUTH } from "../_constants";

/**
 * Login Success
 *
 * MKT-486 — this app NOTIFIES, it does not track. The canonical GA4 `login`
 * event is emitted by the HOST (site_next / alchemist), which owns the top-level
 * GTM container; we only hand it the `method` it cannot know. Emitting from here
 * would push into the IFRAME's dataLayer (separate GA4 session, `page_location`
 * = auth.ingresse.com), which is precisely what MKT-486 undoes.
 *
 * Beware: `update` also dispatches this, so it fires again when the user fills the
 * "incomplete profile" form — after authenticating, and always with the default
 * `email` even for a social login. The service latches the first notification per
 * journey, so that repeat is a no-op instead of a second GA4 `login`.
 *
 * @param {object} data - user data
 * @param {('email'|'facebook'|'google'|'apple'|'facebank')} [method='email']
 */
const successLogin = (data, method = "email") => {
    postMessageService.authLoginSuccess(null, method);

    return {
        type: AUTH.LOGIN_SUCCESS,
        data: data,
    };
};

const isIngresseDomain = (isIngresse) => {
    return {
        type: AUTH.OUT_INGRESSE_DOMAIN,
        payload: isIngresse,
    };
};

/**
 * Register or Update Failure
 */
const resetRegisterError = () => {
    return {
        type: AUTH.REGISTER_FAILURE_CLEAN,
        error: "",
    };
};

/**
 * Login
 *
 * @param {string} email
 * @param {string} password
 * @param {object} device
 * @param {boolean} rememberMe - optional
 * @param {string} turnstileToken - optional
 * @param {object} optionalData - optional
 *
 * @return {function} dispatch
 */
const login = (email, password, device, rememberMe, turnstileToken, isAwa, optionalData = {}, otpCode = null) => {
    /**
     * Login Failure
     *
     * @param {object} error
     */
    const failure = (error) => {
        postMessageService.authLoginFailure();

        return {
            type: AUTH.LOGIN_FAILURE,
            error: error,
        };
    };

    return (dispatch) => {
        authService
            .login(email, password, device, settingsService.get().companyLogin, turnstileToken, isAwa, otpCode)
            .then(
                (response) => {
                    if (response.data?.nextStep === 'otp') {
                        dispatch({
                            type: AUTH.AWA_OTP_REQUIRED,
                            reason: response.data?.code || response.data?.message || null,
                        });
                        return;
                    }

                    if (response.data?.nextStep === 'password') {
                        dispatch({
                            type: AUTH.AWA_PASSWORD_REQUIRED,
                            reason: response.data?.code || response.data?.message || null,
                        });
                        return;
                    }

                    if (response.status && response.data["0"]) {
                        const { userId, token } = response.data["0"];
                        userService
                            .getById(userId, token)
                            .then((userResponse) => {
                                const user = userResponse;
                                if (user) {
                                    const resolved = Object.assign(user);

                                    dispatch(successLogin(resolved));
                                    dispatch(userActions.setData(resolved));
                                }
                            });

                        dispatch({
                            type: AUTH.LOGIN_COMPANY_SUCCESS,
                            companies: response.data,
                            rememberMe: rememberMe ? true : false,
                        });
                        return;
                    }

                    userService.getData().then(
                        (userData) => {
                            const resolved = Object.assign(
                                {},
                                response.data,
                                userData,
                                optionalData
                            );

                            dispatch(successLogin(resolved));

                            dispatch(userActions.setData(resolved));
                        },
                        (error) => {
                            dispatch(failure(error));
                        }
                    );
                },
                (error) => {
                    dispatch(failure(error));
                }
            );
    };
};

/**
 * Facebook login
 *
 * @param {object} userData
 *
 * @return {function} dispatch
 */
const fbLogin = (userData) => {
    return (dispatch) => {
        if (!userData || typeof userData !== "object") {
            dispatch({
                type: AUTH.LOGIN_FAILURE,
                error: "auth:login-failure:no-user-data",
            });

            return;
        }

        // MKT-486 — this path dispatches LOGIN_SUCCESS directly (not through
        // `successLogin`), so it used to emit NO postMessage at all: the host had
        // no way to know a Facebook login happened. Notify it explicitly, or every
        // social login would be missing (or mislabeled as `email`) in GA4.
        postMessageService.authLoginSuccess(null, "facebook");

        dispatch({
            type: AUTH.LOGIN_SUCCESS,
            data: userData,
        });
    };
};

/**
 * Facebook Register
 *
 * @param {object} userData
 *
 * @return {function} dispatch
 */
const fbRegister = (userData) => {
    return (dispatch) => {
        dispatch({
            type: AUTH.FACEBOOK_REGISTER,
            data: userData,
        });
    };
};

/**
 * Logout
 *
 * @return {function} dispatch
 */
const logout = () => {
    authService.logout();
    userService.clearData();
    postMessageService.authLogout();

    return (dispatch) => {
        dispatch(oauthActions.clearCredentials());
        dispatch({
            type: AUTH.LOGOUT,
        });
    };
};

/**
 * Register
 *
 * @param {object}  registerData
 *
 * @return {function} dispatch
 */
const register = (registerData) => {
    /**
     * Dispatch
     */
    return (dispatch) => {
        dispatch(resetRegisterError());
        authService
            .register({
                ...registerData,
                token: registerData.inactiveToken,
            })
            .then(
                (response) => {
                    const toContext = Object.assign(
                        {},
                        response.data,
                        registerData
                    );

                    const newContext = toContext.document
                        ? Object.assign({}, toContext, {
                              document: { number: toContext.document },
                              password: null,
                          })
                        : toContext;

                    const registrationData = Object.assign({}, newContext);
                    delete registrationData.token;
                    delete registrationData.userId;

                    dispatch(userActions.setData(registrationData));
                    dispatch({ type: AUTH.REGISTER_SUCCESS, data: registrationData });

                    // MKT-486 — notify the host that THIS journey is a sign_up.
                    // The host latches it and emits the canonical `sign_up` on the
                    // later `done`, which is the only message carrying the resolved
                    // identity (registration does not authenticate: the app routes
                    // to /login and the user signs in manually).
                    postMessageService.authRegisterSuccess(null, "email");
                },
                (error) => {
                    dispatch({ type: AUTH.REGISTER_FAILURE, error });
                }
            );
    };
};

/**
 * Register
 *
 * @param {object}  registerData
 * @param {object}  device
 * @param {boolean} shouldLogIn
 *
 * @return {function} dispatch
 */
const update = (updateData, userData) => {
    /**
     * Dispatch
     */
    return (dispatch) => {
        dispatch(resetRegisterError());
        authService.update(userData.id, updateData).then(
            (response) => {
                const toContext = Object.assign(
                    {},
                    userData,
                    response.data,
                    updateData
                );

                dispatch(successLogin(toContext));

                const newContext = toContext.document
                    ? Object.assign({}, toContext, {
                          document: { number: toContext.document },
                      })
                    : toContext;

                dispatch(userActions.setData(newContext));
            },
            (error) => {
                dispatch({ type: AUTH.UPDATE_FAILURE, error });
            }
        );
    };
};

/**
 * Get Credentials
 *
 * @return {object} credentials
 */
const getCredentials = () => {
    return (dispatch) => {
        const credentials = authService.getCredentials();

        dispatch({
            type: AUTH.GET_CREDENTIALS,
            credentials,
        });
    };
};

/**
 * Set Credentials
 *
 * @param {object} credentials
 *
 * @return {object} credentials
 */
const setCredentials = (credentials) => {
    authService.setCredentials(credentials);

    return (dispatch) => {
        dispatch({
            type: AUTH.SET_CREDENTIALS,
            credentials,
        });
    };
};

/**
 * Select Company
 *
 * @param {object} company
 * @param {array}  companies - optional
 *
 * @return {function} dispatch
 */
const selectCompany = (company, companies = []) => {
    postMessageService.authCompanyLoginSuccess(company);
    // postMessageService.authDone(company, companies);

    return (dispatch) => {
        dispatch({
            type: AUTH.COMPANY_SELECTED,
            company: company,
        });
    };
};

/**
 * Confirm
 *
 * @return {function} dispatch
 */
const confirm = () => {
    postMessageService.authConfirm();

    return (dispatch) => {
        dispatch({
            type: AUTH.CONFIRM,
        });
    };
};

/**
 * Social Login Success
 *
 * @param {object} data - user data
 */
const socialSuccess = (data) => {
    // MKT-486 — Facebank social flow. Dispatches LOGIN_SUCCESS directly (not via
    // `successLogin`), so the `method` has to be named here.
    postMessageService.authLoginSuccess(null, "facebank");

    return {
        type: AUTH.LOGIN_SUCCESS,
        data,
    };
};

/**
 * Social Login Error
 *
 * @param {object} error - login error
 */
const socialError = (error) => {
    postMessageService.authLoginFailure();

    return {
        type: AUTH.LOGIN_FAILURE,
        error,
    };
};

/**
 * Clean error
 */
const cleanError = () => {
    return (dispatch) => {
        dispatch({
            type: AUTH.CLEAN_ERROR,
        });
    };
};

/* Export Redux Actions */
export const authActions = {
    login,
    fbLogin,
    fbRegister,
    logout,
    register,
    update,
    confirm,
    getCredentials,
    isIngresseDomain,
    setCredentials,
    selectCompany,
    socialSuccess,
    socialError,
    cleanError,
    successLogin,
};
